import { useSessionDataStore } from "@hooks/useSessionDataStore";
import {
    getDeviceBrowser,
    getDeviceOSName,
    getDeviceType,
} from "./userAgentUtils";
import { getPortalConfig } from "./configurationUtils";
import {
    AdditionalProperties,
    DimensionSet,
    EmfHttpStatusCodeMetric,
    EmfLogOptions,
    EmfMetric,
} from "@customTypes/logging";
import { EmfConstants } from "@constants/logging";

export const getDefaultDimensionSets = (): DimensionSet[] => {
    const browser = getDeviceBrowser();
    return [
        {
            [EmfConstants.DIMENSIONS.BROWSER]: browser,
        },
    ];
};

export const getCommonDimensions = (): DimensionSet => ({
    [EmfConstants.DIMENSIONS.BROWSER]: getDeviceBrowser(),
});

export const getCommonProperties = (): AdditionalProperties => ({
    PortalArn: getPortalConfig().portalArn,
    VisitId: window.visitId,
    UserSub: window.userSub,
    SessionId: useSessionDataStore.getState().sessionID,
    DeviceType: getDeviceType(),
    DeviceOS: getDeviceOSName(),
    // EMF batches logs, so the EMF log emit time and the actual event time may differ.
    EventTimestamp: Date.now(),
});

export const createEmfLog = ({
    namespace,
    dimensionSets,
    metrics,
    timestamp = Date.now(),
    additionalProperties = {},
}: EmfLogOptions): Record<string, unknown> => {
    const emfMetrics = metrics.map((metric) => ({
        Name: metric.name,
        ...(metric.unit && { Unit: metric.unit }),
    }));

    const metricValues = metrics.reduce<Record<string, number>>(
        (collectedValues, metric) => ({
            ...collectedValues,
            [metric.name]: metric.value,
        }),
        {}
    );

    const emfLog = {
        _aws: {
            Timestamp: timestamp,
            CloudWatchMetrics: [
                {
                    Namespace: namespace,
                    Dimensions: dimensionSets.map((set) => Object.keys(set)),
                    Metrics: emfMetrics,
                },
            ],
        },
    };

    const allDimensions = dimensionSets.reduce<DimensionSet>(
        (combinedDimensions, set) => ({
            ...combinedDimensions,
            ...set,
        }),
        {}
    );

    // https://code.amazon.com/packages/KatalLoggerCloudWatchBackend/blobs/mainline/--/README.md
    return {
        // This has to be added by the client side logger
        emfLog: {
            ...emfLog,
            ...allDimensions,
            ...metricValues,
            ...additionalProperties,
        },
    };
};

export const createEmfFormattedMetric = (metric: EmfMetric) => {
    const additionalProperties: AdditionalProperties = {
        ...getCommonProperties(),
        ...getCommonDimensions(),
    };

    return createEmfLog({
        namespace: EmfConstants.NAMESPACE,
        dimensionSets: metric.dimensions
            ? [metric.dimensions]
            : getDefaultDimensionSets(),
        metrics: [metric],
        additionalProperties,
    });
};

export const createEmfHttpStatusCodeMetric = (
    metric: EmfHttpStatusCodeMetric
) => {
    const defaultDimensionSets: DimensionSet[] = [
        {
            [EmfConstants.DIMENSIONS.OPERATION]: metric.name,
            [EmfConstants.DIMENSIONS.HTTP_STATUS_CODE]:
                metric.httpStatusCode.toString(),
        },
    ];

    const additionalProperties: AdditionalProperties = {
        ...getCommonProperties(),
        ...getCommonDimensions(),
        HttpStatusCode: metric.httpStatusCode,
    };

    return createEmfLog({
        namespace: EmfConstants.NAMESPACE,
        dimensionSets: metric.dimensions
            ? [metric.dimensions]
            : defaultDimensionSets,
        metrics: [metric],
        additionalProperties,
    });
};
